<template>
  <div class="cms-stories">
    <cms-page-header
      :title="$t('cms.stories.title')"
      :create-text="$t('cms.stories.createNew')"
      @create="navigateToCreate"
    />

    <cms-filter-bar>
      <!-- <cms-filter-group :label="$t('cms.stories.filters.language')">
        <select v-model="selectedLanguage" class="cms-select">
          <option v-for="language in languageOptions" :key="language.value" :value="language.value">
            {{ language.label }}
          </option>
        </select>
      </cms-filter-group> -->

      <cms-filter-group :label="$t('cms.stories.status')">
        <select v-model="selectedStatus" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option v-for="(label, key) in statusOptions" :key="key" :value="key">
            {{ label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.stories.category')">
        <select v-model="selectedCategory" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option v-for="category in categoryOptions" :key="category.value" :value="category.value">
            {{ category.label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.stories.author')">
        <select v-model="selectedAuthor" class="cms-select">
          <option value="">{{ $t('general.all') }}</option>
          <option v-for="author in authorOptions" :key="author.value" :value="author.value">
            {{ author.label }}
          </option>
        </select>
      </cms-filter-group>

      <!-- <cms-filter-group :label="$t('cms.stories.filters.tag')">
        <input
          v-model="selectedTag"
          type="text"
          class="cms-input"
          :placeholder="$t('cms.stories.tagsPlaceholder')"
        />
      </cms-filter-group> -->

      <cms-filter-group :label="$t('general.search')">
        <input
          v-model="searchQuery"
          type="text"
          class="cms-input"
          :placeholder="$t('cms.stories.searchPlaceholder')"
        />
      </cms-filter-group>

      <cms-filter-group :label="''">
        <div class="cms-toggle-group">
          <label class="cms-checkbox-item">
            <input v-model="onlyFeatured" type="checkbox" class="cms-checkbox" />
            <span>{{ $t('cms.stories.filters.featured') }}</span>
          </label>
          <label class="cms-checkbox-item">
            <input v-model="onlyPublic" type="checkbox" class="cms-checkbox" />
            <span>{{ $t('cms.stories.filters.public') }}</span>
          </label>
        </div>
      </cms-filter-group>

      <cms-filter-group :label="''">
        <button class="cms-btn cms-btn-secondary" type="button" :disabled="isLoading" @click="loadStories">
          <v-icon icon="refresh" size="sm" />
          <span>{{ $t('general.refresh') }}</span>
        </button>
      </cms-filter-group>
    </cms-filter-bar>

    <div v-if="errorMessage" class="cms-stories-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="cms-btn-link" @click="loadStories">{{ $t('general.retry') }}</button>
    </div>

    <div v-if="isLoading" class="cms-stories-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
    </div>

    <cms-empty-state
      v-else-if="filteredStories.length === 0"
      :message="$t('cms.stories.list.emptyStateTitle')"
      :hint="$t('cms.stories.list.emptyStateHint')"
    />

    <cms-grid v-else min-width="360px">
      <cms-card
        v-for="story in filteredStories"
        :key="story.id"
        clickable
        class="cms-story-card"
        @click="navigateToEdit(story.id)"
      >
        <template #status>
          <cms-status-badge :status="story.status">
            {{ statusOptions[story.status] || story.status }}
          </cms-status-badge>
        </template>

        <template #actions>
          <cms-icon-button
            :icon="story.isPublic ? 'eye-blocked' : 'eye'"
            :variant="story.isPublic ? 'secondary' : 'primary'"
            :disabled="publishingId === story.id"
            :tooltip="story.isPublic ? $t('cms.stories.unpublishSuccess') : $t('cms.stories.publishSuccess')"
            @click.stop="togglePublish(story)"
          />
          <cms-icon-button
            icon="pencil"
            variant="primary"
            :tooltip="$t('general.edit')"
            @click.stop="navigateToEdit(story.id)"
          />
          <cms-icon-button
            icon="trash"
            variant="danger"
            :disabled="deletingId === story.id"
            :tooltip="$t('general.delete')"
            @click.stop="deleteStory(story)"
          />
        </template>

        <div v-if="story.featuredImage" class="cms-story-preview" :style="{ backgroundImage: `url(${story.featuredImage})` }" />

        <div class="cms-story-header">
          <h3 class="cms-story-title">{{ story.title }}</h3>
          <span v-if="story.isFeatured" class="cms-story-featured">{{ $t('cms.activities.featuredBadge') }}</span>
        </div>

        <p v-if="story.excerpt" class="cms-story-excerpt">
          {{ story.excerpt }}
        </p>

        <ul class="cms-story-meta">
          <li>
            <v-icon icon="user" size="sm" />
            <span>{{ story.authorName }}</span>
          </li>
          <li v-if="story.categoryLabel">
            <v-icon icon="tag" size="sm" />
            <span>{{ story.categoryLabel }}</span>
          </li>
          <li>
            <v-icon icon="calendar" size="sm" />
            <span>{{ story.publishedAtLabel || story.updatedAtLabel }}</span>
          </li>
        </ul>

        <div v-if="story.tags.length" class="cms-story-tags">
          <span v-for="tag in story.tags" :key="tag" class="cms-story-tag">#{{ tag }}</span>
        </div>

        <template #footer>
          <div class="cms-card-footer">
            <span class="cms-story-date">
              {{ story.updatedAtLabel }}
            </span>
            <span class="cms-story-locale">
              {{ story.locale?.toUpperCase() }}
            </span>
          </div>
        </template>
      </cms-card>
    </cms-grid>

    <cms-pagination
      v-if="paginationMeta"
      :meta="paginationMeta"
      :per-page-options="[9, 18, 27, 36]"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import {
  CmsPageHeader,
  CmsFilterBar,
  CmsFilterGroup,
  CmsGrid,
  CmsCard,
  CmsStatusBadge,
  CmsIconButton,
  CmsEmptyState,
  CmsPagination,
} from '@/components/cms';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface CmsStoryCard {
  id: number;
  title: string;
  excerpt: string;
  authorName: string;
  categoryLabel: string;
  tags: string[];
  isFeatured: boolean;
  isPublic: boolean;
  publishedAt: string | null;
  publishedAtLabel: string;
  updatedAt: string | null;
  updatedAtLabel: string;
  status: 'draft' | 'published' | 'scheduled';
  locale: string;
  featuredImage?: string;
}

export default defineComponent({
  name: 'TheCmsStories',
  components: {
    CmsPageHeader,
    CmsFilterBar,
    CmsFilterGroup,
    CmsGrid,
    CmsCard,
    CmsStatusBadge,
    CmsIconButton,
    CmsEmptyState,
    CmsPagination,
    VIcon,
  },
  setup() {
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const { locales, resolveStoredLocale } = useAppLanguages();
    const currentLocale = ref(resolveStoredLocale(localStorage.getItem('language')));

    const stories = ref<CmsStoryCard[]>([]);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const publishingId = ref<number | null>(null);
    const deletingId = ref<number | null>(null);

    // Pagination state
    const currentPage = ref(1);
    const perPage = ref(9);
    const paginationMeta = ref<{
      currentPage: number;
      lastPage: number;
      perPage: number;
      from: number | null;
      to: number | null;
      total: number;
    } | null>(null);

    const selectedLanguage = ref(currentLocale.value);
    const selectedCategory = ref('');
    const selectedStatus = ref('');
    const selectedAuthor = ref('');
    const selectedTag = ref('');
    const onlyFeatured = ref(false);
    const onlyPublic = ref(false);
    const searchQuery = ref('');

    const authors = ref<{ value: string; label: string }[]>([]);

    const languageOptions = computed(() =>
      locales.value.map((locale) => ({
        value: locale,
        label: locale.toUpperCase(),
      })),
    );

    const statusOptions = computed(() => ({
      draft: t('cms.stories.statusOptions.draft'),
      published: t('cms.stories.statusOptions.published'),
      scheduled: t('cms.stories.statusOptions.scheduled'),
    }));

    const storyCategories = ref<Array<{ id: number; value: string; label: string }>>([]);

    const categoryOptions = computed(() => {
      const options = [{ value: '', label: t('general.all') }];
      const addOptions = (items: typeof storyCategories.value) => {
        for (const cat of items) {
          options.push({ value: cat.value, label: cat.label });
        }
      };
      addOptions(storyCategories.value);
      return options;
    });

    const loadStoryCategories = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.storyCategoryService.listCmsStoryCategories(agencySlug.value, {
          filters: { as_tree: false },
        });
        const list = extractListData(response);
        const items = Array.isArray(list) ? list : [list].filter(Boolean);
        const buildFlat = (cats: any[], parentId: number | null, depth: number): any[] => {
          const result: any[] = [];
          for (const cat of cats) {
            const n = normalizeResource(cat) || cat;
            const rawPid = n.parent_id ?? n.parentId ?? null;
            const pid = rawPid != null ? Number(rawPid) : null;
            if (pid == parentId) {
              const tr = Array.isArray(n.translations) ? n.translations[0] : n.translations || {};
              const name = tr.name ?? n.name ?? '';
              const indent = '—'.repeat(depth);
              result.push({
                id: n.id ?? n.attributes?.id,
                value: String(n.id ?? n.attributes?.id),
                label: indent ? `${indent} ${name}` : name,
              });
              result.push(...buildFlat(items, n.id ?? n.attributes?.id, depth + 1));
            }
          }
          return result;
        };
        storyCategories.value = buildFlat(items, null, 0);
      } catch (error) {
        console.error('Failed to load story categories', error);
        storyCategories.value = [];
      }
    };

    const authorOptions = computed(() => authors.value);

    // Since filtering is now done server-side, filteredStories just returns stories
    const filteredStories = computed(() => stories.value);
    const extractListData = (response: any): any[] => {
      if (!response) return [];
      const candidates = [
        response?.data?.data,
        response?.data?.data?.data,
        response?.data,
        response,
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const formatDateTime = (value: string | null | undefined) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleString();
    };

    const computeStatus = (record: any): 'draft' | 'published' | 'scheduled' => {
      const isPublic = !!record.isPublic;
      const publishedAt = record.publishedAt ? new Date(record.publishedAt) : null;
      if (!isPublic) {
        return 'draft';
      }
      if (publishedAt && publishedAt.getTime() > Date.now()) {
        return 'scheduled';
      }
      return 'published';
    };

    const extractTranslations = (story: any) => {
      const relationship = extractRelationship(story, 'translations');
      const normalizedRelationship = normalizeResource(relationship);
      if (Array.isArray(normalizedRelationship)) {
        return normalizedRelationship;
      }
      if (Array.isArray(story?.translations)) {
        return story.translations;
      }
      if (normalizedRelationship) {
        return [normalizedRelationship];
      }
      return [];
    };

    const extractMediaItems = (story: any) => {
      const relationship = extractRelationship(story, 'media');
      const normalized = normalizeResource(relationship);
      if (Array.isArray(normalized)) {
        return normalized;
      }
      if (Array.isArray(story?.media)) {
        return story.media;
      }
      return [];
    };
    const mapStoryRecord = (item: any): CmsStoryCard => {
      const normalized = normalizeResource(item) || item;
      
      const translations = extractTranslations(normalized);
      const translation =
        translations.find((tr: any) => (tr?.locale || '').toLowerCase() === selectedLanguage.value) ||
        translations[0] ||
        {};

      const authorName =
        (() => {
          const teamMember = normalized?.teamMember ?? normalized?.team_member;
          const member = normalizeResource(teamMember) || teamMember;
          if (member) {
            const translations = extractRelationship(member, 'translations');
            const normalizedTranslations = normalizeResource(translations);
            const translationList = Array.isArray(normalizedTranslations)
              ? normalizedTranslations
              : normalizedTranslations
                ? [normalizedTranslations]
                : Array.isArray(member?.translations)
                  ? member.translations
                  : [];
            const translation =
              translationList.find(
                (entry: any) =>
                  (entry?.locale || '').toLowerCase() === selectedLanguage.value,
              ) || translationList[0];
            return translation?.name || translation?.attributes?.name || '-';
          }
          return normalized?.author?.user?.name || '-';
        })();

      const mediaItems = extractMediaItems(normalized);

      const tags = Array.isArray(normalized?.tags)
        ? normalized.tags.filter((tag: string) => !!tag)
        : typeof normalized?.tags === 'string'
          ? normalized.tags.split(',').map((tag: string) => tag.trim())
          : [];

      const status = computeStatus(normalized);

      const categoryLabel = (() => {
        const categories = normalized.categories ?? normalized.attributes?.categories;
        if (Array.isArray(categories) && categories.length > 0) {
          return categories
            .map((c: any) => {
              const cat = normalizeResource(c) || c;
              if (cat.path) return cat.path;
              const tr = Array.isArray(cat.translations) ? cat.translations[0] : cat.translations || {};
              return tr.name ?? cat.name ?? '';
            })
            .filter(Boolean)
            .join(', ');
        }
        const cat = normalized.category || normalized.attributes?.category;
        if (!cat) return '';
        const normalizedCat = normalizeResource(cat) || cat;
        if (normalizedCat.path) return normalizedCat.path;
        const tr = Array.isArray(normalizedCat.translations)
          ? normalizedCat.translations[0]
          : normalizedCat.translations || {};
        return tr.name ?? normalizedCat.name ?? '';
      })();

      return {
        id: normalized.id,
        title: translation?.title || normalized.title || '-',
        excerpt: translation?.excerpt || '',
        authorName,
        categoryLabel,
        tags,
        isFeatured: normalized.isFeatured,
        isPublic: normalized.isPublic,
        publishedAt: normalized.publishedAt || null,
        publishedAtLabel: formatDateTime(normalized.publishedAt),
        updatedAt: normalized.updatedAt || null,
        updatedAtLabel: formatDateTime(normalized.updatedAt),
        status,
        locale: (translation?.locale || selectedLanguage.value || 'en').toLowerCase(),
        featuredImage: mediaItems[0]?.originalUrl || '',
      };
    };

    const buildFilters = () => {
      const filters: Record<string, any> = {
        language: selectedLanguage.value || currentLocale.value,
      };

      if (selectedCategory.value) {
        const catId = Number(selectedCategory.value);
        if (!Number.isNaN(catId)) {
          filters.cms_story_category_id = catId;
        }
      }

      if (selectedAuthor.value) {
        const teamMemberId = Number(selectedAuthor.value);
        if (!Number.isNaN(teamMemberId)) {
          filters.cms_team_member_id = teamMemberId;
        }
      }

      if (selectedTag.value.trim()) {
        filters.tag = selectedTag.value.trim();
      }

      if (onlyFeatured.value) {
        filters.is_featured = 1;
      }

      if (onlyPublic.value) {
        filters.is_public = 1;
      }

      if (searchQuery.value.trim()) {
        filters.search = searchQuery.value.trim();
      }

      return filters;
    };

    const loadStories = async () => {
      if (!agencySlug.value) {
        return;
      }

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const response = await api.storyService.listCmsStories(agencySlug.value, {
          page: currentPage.value,
          perPage: perPage.value,
          filters: buildFilters(),
          order: { key: 'published_at', direction: 'desc' },
        });

        const list = extractListData(response);
        stories.value = list.map(mapStoryRecord);
        
        const responseMeta = response?.meta;

        if (responseMeta) {
          const currentPageValue =
            Number(
              responseMeta?.currentPage
            ) || currentPage.value;
          const lastPageValue =
            Number(
              responseMeta?.lastPage
            ) || 1;
          const perPageValue =
            Number(
              responseMeta?.perPage
            ) || perPage.value;
          const totalValue =
            Number(
              responseMeta?.total
            ) || 0;

          paginationMeta.value = {
            currentPage: currentPageValue,
            lastPage: lastPageValue,
            perPage: perPageValue,
            from: responseMeta?.from ?? null,
            to: responseMeta?.to ?? null,
            total: totalValue,
          };
          currentPage.value = paginationMeta.value.currentPage;
          perPage.value = paginationMeta.value.perPage;
        } else {
          paginationMeta.value = null;
        }
      } catch (error: any) {
        console.error('Failed to load CMS stories', error);
        errorMessage.value = error?.response?.data?.message || t('general.errorMessage');
        toast.error(errorMessage.value);
      } finally {
        isLoading.value = false;
      }
    };

    const loadAuthors = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.teamMemberService.listCmsTeamMembers(agencySlug.value, {
          perPage: 200,
          order: { key: 'order', direction: 'asc' },
        });
        const list = extractListData(response);
        authors.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          const translations = extractRelationship(normalized, 'translations');
          const normalizedTranslations = normalizeResource(translations);
          const translationList = Array.isArray(normalizedTranslations)
            ? normalizedTranslations
            : normalizedTranslations
              ? [normalizedTranslations]
              : Array.isArray(normalized?.translations)
                ? normalized.translations
                : [];
          const translation =
            translationList.find(
              (entry: any) =>
                (entry?.locale || '').toLowerCase() === selectedLanguage.value,
            ) || translationList[0] || {};
          const name = translation?.name || translation?.attributes?.name || `#${normalized.id}`;
          return {
            value: `${normalized.id}`,
            label: name,
          };
        });
      } catch (error) {
        console.error('Failed to load team member authors', error);
      }
    };

    const handlePageChange = (page: number) => {
      currentPage.value = page;
      loadStories();
    };

    const handlePerPageChange = (newPerPage: number) => {
      perPage.value = newPerPage;
      currentPage.value = 1; // Reset to first page when changing per page
      loadStories();
    };

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          currentPage.value = 1;
          loadAuthors();
          loadStoryCategories();
          loadStories();
        }
      },
      { immediate: true },
    );

    watch(
      [selectedLanguage, selectedCategory, selectedAuthor, selectedTag, onlyFeatured, onlyPublic],
      () => {
        if (!agencySlug.value) return;
        currentPage.value = 1;
        loadStories();
      },
    );

    let searchDebounce: ReturnType<typeof setTimeout> | null = null;
    watch(
      () => searchQuery.value,
      () => {
        if (!agencySlug.value) return;
        if (searchDebounce) {
          clearTimeout(searchDebounce);
        }
        searchDebounce = setTimeout(() => {
          currentPage.value = 1;
          loadStories();
        }, 400);
      },
    );

    const navigateToCreate = () => {
      router.push({ name: 'cms.stories.create' });
    };

    const navigateToEdit = (storyId: number) => {
      router.push({ name: 'cms.stories.edit', params: { id: storyId } });
    };

    const togglePublish = async (story: CmsStoryCard) => {
      if (!agencySlug.value) return;

      const nextState = !story.isPublic;
      publishingId.value = story.id;

      try {
        await api.storyService.publishCmsStory(agencySlug.value, story.id, {
          is_public: nextState,
          published_at: story.publishedAt || new Date().toISOString(),
        });
        stories.value = stories.value.map((item) =>
          item.id === story.id
            ? {
                ...item,
                isPublic: nextState,
                status: nextState ? 'published' : 'draft',
              }
            : item,
        );
        toast.success(nextState ? t('cms.stories.publishSuccess') : t('cms.stories.unpublishSuccess'));
      } catch (error: any) {
        console.error('Failed to toggle publish state', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        publishingId.value = null;
      }
    };

    const deleteStory = async (story: CmsStoryCard) => {
      if (!agencySlug.value) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) {
        return;
      }

      deletingId.value = story.id;
      try {
        await api.storyService.deleteCmsStory(agencySlug.value, story.id);
        stories.value = stories.value.filter((item) => item.id !== story.id);
        toast.success(t('cms.stories.deleteSuccess'));
      } catch (error: any) {
        console.error('Failed to delete story', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        deletingId.value = null;
      }
    };

    return {
      stories,
      filteredStories,
      selectedLanguage,
      selectedCategory,
      selectedStatus,
      selectedAuthor,
      selectedTag,
      onlyFeatured,
      onlyPublic,
      searchQuery,
      statusOptions,
      languageOptions,
      categoryOptions,
      authorOptions,
      isLoading,
      errorMessage,
      publishingId,
      deletingId,
      loadStories,
      navigateToCreate,
      navigateToEdit,
      togglePublish,
      deleteStory,
      paginationMeta,
      handlePageChange,
      handlePerPageChange,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

// ============================================
// VIEW-SPECIFIC STYLES
// ============================================
// Only styles unique to this view

.cms-stories {
  color: $cms-text-primary;
}


.cms-toggle-group {
  display: flex;
  gap: $cms-spacing-sm;
  flex-wrap: wrap;
}


.cms-stories-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-xxl 0;
  gap: $cms-spacing-sm;
  color: $cms-text-secondary;
}

.cms-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: $cms-primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}


.cms-stories-error {
  display: flex;
  align-items: center;
  gap: $cms-spacing-md;
  padding: $cms-spacing-md;
  margin-bottom: $cms-spacing-md;
  background: $cms-danger-light;
  border: 1px solid $cms-danger-border;
  border-radius: $cms-radius-md;
  color: $cms-danger-color;
}

.cms-btn-link {
  background: none;
  border: none;
  padding: 0;
  color: $cms-primary-color;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}


.cms-story-card {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-sm;
}

.cms-story-preview {
  width: 100%;
  height: 160px;
  border-radius: $cms-radius-md;
  background-size: cover;
  background-position: center;
  margin-bottom: $cms-spacing-sm;
}

.cms-story-header {
  display: flex;
  align-items: center;
  gap: $cms-spacing-sm;
}

.cms-story-title {
  font-size: $cms-font-size-lg;
  font-weight: 600;
  margin: 0;
  color: $cms-text-primary;
}

.cms-story-featured {
  padding: $cms-spacing-xxs $cms-spacing-xs;
  background: $cms-warning-light;
  color: $cms-warning-color;
  border-radius: $cms-radius-sm;
  font-size: $cms-font-size-xs;
  font-weight: 600;
  text-transform: uppercase;
}

.cms-story-excerpt {
  color: $cms-text-secondary;
  margin: 0;
  line-height: 1.45;
}

.cms-story-meta {
  list-style: none;
  padding: 0;
  margin: $cms-spacing-sm 0;
  display: flex;
  flex-wrap: wrap;
  gap: $cms-spacing-sm;

  li {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: $cms-font-size-sm;
    color: $cms-text-secondary;
  }
}

.cms-story-tags {
  display: flex;
  flex-wrap: wrap;
  gap: $cms-spacing-xxs;
}

.cms-story-tag {
  padding: $cms-spacing-xxs $cms-spacing-xs;
  background: rgba(0, 0, 0, 0.05);
  border-radius: $cms-radius-sm;
  font-size: $cms-font-size-xs;
  color: $cms-text-secondary;
}

.cms-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
}

.cms-story-locale {
  font-weight: 600;
  color: $cms-primary-color;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
